import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { itineraryApi } from '../api/itinerary.api';
import { apiErrorCode } from '../api/client';
import { toast } from '../components/ui/Toast';
import { duplicateEntry, moveEntry, stopIds, type HistoryEntry } from '../utils/itineraryHistoryEntries';
import type { ItineraryHistory } from './useItineraryHistory';
import type { Activity, ItineraryDay } from '../types';

/**
 * Moving stops between days, shared by the three surfaces that can do it: the
 * itinerary's list view (drop on a day chip), its timeline view (drop into
 * another day's stack) and the map's routes panel. They differ only in how the
 * gesture is captured — what happens afterwards has to be identical, or the
 * same drag means different things depending on which tab you are looking at.
 *
 * The server is the authority on the result: it re-sorts the target day by time
 * and rebuilds the "continues from" mirrors of both days, so the response
 * replaces the optimistic guess rather than being merged into it.
 */

/** The cards a batch refuses to touch, each with a reason worth showing. */
const REFUSALS: Record<string, string> = {
  CANNOT_MOVE_ORIGIN:            'itinerary.move.errorOrigin',
  CANNOT_DUPLICATE_ORIGIN:       'itinerary.move.errorOrigin',
  CANNOT_MOVE_STAY_NIGHT:        'itinerary.move.errorStayNight',
  CANNOT_DUPLICATE_BOOKING_CARD: 'itinerary.move.errorBookingCard',
};

interface Options {
  tripId: string;
  days: ItineraryDay[];
  setDays: React.Dispatch<React.SetStateAction<ItineraryDay[]>>;
  /**
   * ADR-0015: moves and duplicates can be undone. Callers that record them pass
   * the history, and its `setDays` as `setDays`.
   */
  history?: ItineraryHistory;
}

export interface CrossDayMove {
  /**
   * `atIndex` is the slot the card was dropped into, among the target day's
   * non-origin stops. Omit it when the gesture carries no position — dropping
   * on a day chip in the strip, or picking a day from the bulk menu.
   */
  moveToDay: (activityIds: string[], targetDayId: string, atIndex?: number) => Promise<void>;
  duplicateToDay: (activityIds: string[], targetDayId: string) => Promise<void>;
  pending: boolean;
}

/** The origin marker is pinned first and is not part of the drop positions. */
const nonOrigin = (day: ItineraryDay): Activity[] => day.activities.filter(a => !a.isOrigin);

export function useCrossDayMove({ tripId, days, setDays, history }: Options): CrossDayMove {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);

  const applyServerDays = useCallback((updated: ItineraryDay[]) => {
    const byId = new Map(updated.map(d => [d.id, d]));
    setDays(prev => prev.map(d => byId.get(d.id) ?? d));
  }, [setDays]);

  const fail = useCallback((err: unknown, snapshot: ItineraryDay[], fallbackKey: string) => {
    setDays(snapshot);
    const key = REFUSALS[apiErrorCode(err) ?? ''];
    toast(key ? t(key) : t(fallbackKey), 'error');
  }, [setDays, t]);

  const move = useCallback(async (
    activityIds: string[], targetDayId: string, atIndex?: number,
  ): Promise<HistoryEntry | null> => {
    // Read the current days and build both the rollback snapshot and the moved
    // cards here, not inside the state updater: an updater must stay pure, and
    // React can call it more than once (StrictMode) — which would collect the
    // moving cards twice.
    const snapshot = days;
    const moving = days.flatMap(d => d.activities.filter(a => activityIds.includes(a.id)));
    // Nothing matched — a co-traveller moved or deleted it first.
    if (moving.length === 0) return null;

    setDays(prev => prev.map(day => {
      const kept = day.activities.filter(a => !activityIds.includes(a.id));
      if (day.id !== targetDayId) return { ...day, activities: kept };
      const origin = kept.filter(a => a.isOrigin);
      const rest   = kept.filter(a => !a.isOrigin);
      const at = atIndex ?? rest.length;
      return { ...day, activities: [...origin, ...rest.slice(0, at), ...moving, ...rest.slice(at)] };
    }));

    setPending(true);
    try {
      const updated = await itineraryApi.moveActivities(tripId, activityIds, targetDayId);
      applyServerDays(updated);
      const target = updated.find(d => d.id === targetDayId);
      let targetAfter = stopIds(target);
      const message = t('itinerary.move.moved', { count: moving.length, day: target?.dayNumber ?? '' });

      if (atIndex === undefined) {
        // A drag shows its own result; a pick from the bulk menu does not — the
        // cards leave the day on screen and land where the user isn't looking.
        // With a history the undo toast says it instead.
        if (!history) toast(message, 'success');
      } else if (target) {
        // The server appends and then re-sorts the timed stops, so a drop into
        // the middle of a day needs a second pass to hold its slot.
        const stops   = nonOrigin(target);
        const rest    = stops.filter(a => !activityIds.includes(a.id));
        const desired = [...rest.slice(0, atIndex), ...moving, ...rest.slice(atIndex)].map(a => a.id);
        if (desired.join() !== stops.map(a => a.id).join()) {
          const { activities } = await itineraryApi.reorderActivities(tripId, targetDayId, desired);
          setDays(prev => prev.map(d => (d.id === targetDayId ? { ...d, activities } : d)));
          targetAfter = desired;
        }
      }

      if (!history) return null;
      // A flight or booking card moves its booking, not just the plan: that is
      // not a step to undo, and the steps before it no longer hold.
      if (moving.some(a => a.flightId || a.bookingId)) { history.forget(); return null; }
      return moveEntry(
        { tripId, setDays, getDays: history.getDays },
        { activityIds, targetDayId, before: snapshot, targetAfter, message },
      );
    } catch (err) {
      fail(err, snapshot, 'itinerary.move.failed');
      return null;
    } finally {
      setPending(false);
    }
  }, [tripId, days, setDays, applyServerDays, fail, t, history]);

  const moveToDay = useCallback(async (
    activityIds: string[], targetDayId: string, atIndex?: number,
  ) => {
    if (activityIds.length === 0 || pending) return;
    if (history) await history.run(() => move(activityIds, targetDayId, atIndex));
    else await move(activityIds, targetDayId, atIndex);
  }, [pending, history, move]);

  const duplicate = useCallback(async (activityIds: string[], targetDayId: string): Promise<HistoryEntry | null> => {
    // No optimistic insert: the copies get server-generated ids, and inventing
    // placeholders only to swap them out a moment later buys nothing — the
    // originals stay put, so nothing on screen looks broken while it saves.
    const snapshot = days;
    setPending(true);
    try {
      const updated = await itineraryApi.duplicateActivities(tripId, activityIds, targetDayId);
      applyServerDays(updated);
      const message = t('itinerary.move.duplicated', { count: activityIds.length });
      if (!history) { toast(message, 'success'); return null; }
      const had = new Set(stopIds(snapshot.find(d => d.id === targetDayId)));
      const copyIds = stopIds(updated.find(d => d.id === targetDayId)).filter(id => !had.has(id));
      return duplicateEntry(
        { tripId, setDays, getDays: history.getDays },
        { activityIds, targetDayId, before: snapshot, copyIds, message },
      );
    } catch (err) {
      fail(err, snapshot, 'itinerary.move.duplicateFailed');
      return null;
    } finally {
      setPending(false);
    }
  }, [tripId, days, setDays, applyServerDays, fail, t, history]);

  const duplicateToDay = useCallback(async (activityIds: string[], targetDayId: string) => {
    if (activityIds.length === 0 || pending) return;
    if (history) await history.run(() => duplicate(activityIds, targetDayId));
    else await duplicate(activityIds, targetDayId);
  }, [pending, history, duplicate]);

  return { moveToDay, duplicateToDay, pending };
}
